import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import {
  DeliveryMethod,
  InvoiceInsightsCustomer,
} from 'src/app/_types/billing.types';

export type DeliveryOption = {
  method: DeliveryMethod;
  label: string;
};

export const DELIVERY_OPTIONS: DeliveryOption[] = [
  { method: 'EMAIL', label: $localize`Email` },
  { method: 'POST', label: $localize`Courrier` },
];

export function getDeliveryMethodLabel(method: DeliveryMethod): string {
  return (
    DELIVERY_OPTIONS.find((option) => option.method === method)?.label ?? method
  );
}

/**
 * Read-only delivery method: a bill goes out the way the customer's main
 * member asked for. Without that preference it warns and links to the
 * customer profile, where it is set.
 */
@Component({
  selector: 'app-delivery-method-select',
  template: `
    <input
      class="form-control delivery-method"
      [value]="method() ? getLabel(method()!) : ''"
      placeholder="Non renseignée"
      i18n-placeholder
      disabled
    />
    @if (!method()) {
      <p class="missing-preference">
        <span i18n>Préférence d'envoi du client non renseignée.</span>
        @if (customer()?.userId; as userId) {
          <a
            [routerLink]="['/customers/view/user', userId]"
            target="_blank"
            i18n
            >Renseigner</a
          >
        }
      </p>
    }
  `,
  styles: [
    `
      :host {
        display: block;
        width: 220px;
      }

      /* Match Figma 8456-5530: gray-300 border, 8px radius, gray-500 label. */
      .delivery-method {
        /* As tall as the buttons beside it; stylesheet.css forces inputs to 46px. */
        height: 42px !important;
        padding: 8px 12px;
        font-size: 14px;
        font-weight: 500;
        line-height: 24px;
        color: var(--gray-500);
        background: var(--gray-50);
        border: 1px solid var(--gray-300);
        border-radius: 8px;
        box-shadow: none;
      }

      .missing-preference {
        margin: 4px 0 0;
        font-size: 12px;
        line-height: 18px;
        color: var(--warning-700);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryMethodSelectComponent {
  readonly customer = input<InvoiceInsightsCustomer | null | undefined>(null);

  readonly method = computed(
    () => this.customer()?.communicationMethod ?? null
  );

  readonly getLabel = getDeliveryMethodLabel;
}
